import React from "react";
import { useNavigate } from "react-router-dom";
import { SWACHHATA_QUIZ_OPEN } from "../config/campaigns";
import { isBricsShown, BRICS_PATH, BRICS_CAMPAIGN, BRICS_DATES, BRICS_TITLE } from "./SchoolAdmin/BricsCampaign/bricsConfig";
import "./AnnouncementStrip.css";

const HANDHOLDING_URL =
  "https://aicteindia.webex.com/aicteindia/j.php?MTID=m61d69660d951928b61c8bd1569c3755c";

/*
 * Landing-page announcement bar: one static strip holding three clickable
 * blocks — daily handholding session (left), Swachhata Hi Seva drive (centre,
 * featured: learning material, student quiz, public SWM events) and the
 * total-notifications report (right). On phones the drive block comes first.
 * Once the quiz is closed (config/campaigns.js) the centre block features
 * Special Campaign 6 (E-Waste) instead: its form + E-Waste events.
 *
 * Mixed text is always ONE template string: the Anuvadini translation
 * extension merges adjacent JSX text nodes ("0Notifications").
 */
export default function AnnouncementStrip({ totalNotifications, notificationReportData }) {
  const navigate = useNavigate();
  const goQuiz = () => navigate("/swachhata-hi-seva");
  const goLearn = () => navigate("/swachhata-hi-seva?start=learn");
  const goEvents = () => navigate("/swachhata-hi-seva/events");
  const goBrics = () => navigate(BRICS_PATH);
  const goNotifications = () => navigate("/notification-report", { state: notificationReportData });

  const onKey = (go) => (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go();
    }
  };

  return (
    <section className="ann-strip" aria-label="Announcements">
      <a
        className="ann-block ann-block--session"
        href={HANDHOLDING_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Daily handholding session on Ek Ped Maa Ke Naam 3.0, 9:30 to 10:30 AM. Opens Webex."
      >
        <span className="ann-block__tag">Daily</span>
        <span className="ann-block__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2.5" y="6" width="13" height="12" rx="3" />
            <path d="m15.5 10.5 6-3.5v10l-6-3.5" />
          </svg>
        </span>
        <span className="ann-block__text">
          <strong>Daily Handholding Session</strong>
          <small>Ek Ped Maa Ke Naam 3.0 · 9:30–10:30 AM</small>
        </span>
        <span className="ann-block__cta">Join →</span>
      </a>

      {SWACHHATA_QUIZ_OPEN ? (
      <div
        className="ann-block ann-block--shs"
        role="link"
        tabIndex={0}
        aria-label="Swachhata Hi Seva 2026: Solid Waste Management quiz for students. Click to open."
        onClick={goQuiz}
        onKeyDown={onKey(goQuiz)}
      >
        <span className="ann-block__badge">NEW</span>
        <span className="ann-block__bins" aria-hidden="true"><i /><i /><i /><i /></span>
        <span className="ann-block__text">
          <strong>Swachhata Hi Seva 2026</strong>
          <small>Solid Waste Management Quiz</small>
          <em className="ann-block__slogan">Swachh Paathshala: Yuva for Swachhata</em>
        </span>
        <span className="ann-block__ctas">
          <button type="button" className="ann-block__cta ann-block__cta--ghost" onClick={(e) => { e.stopPropagation(); goLearn(); }}>
            Learning Material
          </button>
          <button type="button" className="ann-block__cta" onClick={(e) => { e.stopPropagation(); goQuiz(); }}>
            Take Quiz →
          </button>
          <button type="button" className="ann-block__cta ann-block__cta--ghost" onClick={(e) => { e.stopPropagation(); goEvents(); }}>
            SWM Events
          </button>
        </span>
      </div>
      ) : isBricsShown() ? (
      <div
        className="ann-block ann-block--shs"
        role="link"
        tabIndex={0}
        aria-label={`${BRICS_CAMPAIGN} (${BRICS_DATES}): ${BRICS_TITLE}. Click to open the form.`}
        onClick={goBrics}
        onKeyDown={onKey(goBrics)}
      >
        <span className="ann-block__badge">LIVE</span>
        <span className="ann-block__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v3M12 18v3M4.2 7.2l2.1 2.1M17.7 14.7l2.1 2.1M3 12h3M18 12h3M4.2 16.8l2.1-2.1M17.7 9.3l2.1-2.1" />
            <circle cx="12" cy="12" r="3.2" />
          </svg>
        </span>
        <span className="ann-block__text">
          <strong>{BRICS_CAMPAIGN}</strong>
          <small>{`${BRICS_DATES} · ${BRICS_TITLE}`}</small>
          <em className="ann-block__slogan">One Week, Seven Actions, One Planet</em>
        </span>
        <span className="ann-block__ctas">
          <button type="button" className="ann-block__cta" onClick={(e) => { e.stopPropagation(); goBrics(); }}>
            Upload Participation →
          </button>
        </span>
      </div>
      ) : null}

      <div
        className="ann-block ann-block--notif"
        role="link"
        tabIndex={0}
        aria-label="Total notifications uploaded. Click to view state-wise report."
        onClick={goNotifications}
        onKeyDown={onKey(goNotifications)}
      >
        <span className="ann-block__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 10v4h4l8 5V5L7 10Z" />
            <path d="M18.5 8.5a5 5 0 0 1 0 7" />
          </svg>
        </span>
        <span className="ann-block__text">
          <strong>{`${(totalNotifications || 0).toLocaleString("en-IN")} Notifications`}</strong>
          <small>State-wise upload report</small>
        </span>
        <span className="ann-block__cta">View →</span>
      </div>
    </section>
  );
}
